import React from "react";
import { getAttachmentFileName } from "@features/bay-hire/hooks/useDownloadBayHireAttachment";
import type { Attachment } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";
import BayHireFileCard from "./BayHireFileCard";

interface BayHireAttachmentsSectionProps {
  attachments: Attachment[];
  isDownloading: (documentUrl: string) => boolean;
  /** Opens the attachment in a new tab (view) - AC29. */
  onView: (attachment: Attachment) => void;
  onDownload: (attachment: Attachment) => void;
}

/**
 * DFT-0027: Figma p10 "Attachments" section - one file card per attachment, each exposing
 * independent view (filename) and download (icon) controls, per DFT-0050 AC29. Previously each
 * attachment rendered as bare, non-actionable text (its attachmentType only), so uploaded
 * documents could not be opened at all.
 */
const BayHireAttachmentsSection: React.FC<BayHireAttachmentsSectionProps> = ({
  attachments,
  isDownloading,
  onView,
  onDownload,
}) => {
  const { t } = useTranslation();

  return (
    <section
      className="border border-gray-200 rounded-md bg-white p-6 mt-6"
      data-testid="bay-hire-detail-attachments-section"
    >
      <h3 className="text-base font-semibold text-gray-900 mb-3">
        {t("bay-hire.detail.attachments")}
      </h3>
      <ul className="space-y-3" data-testid="bay-hire-detail-attachments">
        {attachments.map((attachment) => (
          <li key={attachment.id}>
            <BayHireFileCard
              fileName={getAttachmentFileName(attachment)}
              caption={attachment.attachmentType}
              isDownloading={isDownloading(attachment.documentUrl)}
              onView={() => onView(attachment)}
              onDownload={() => onDownload(attachment)}
              testId={`bay-hire-detail-attachment-${attachment.id}`}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default BayHireAttachmentsSection;
